import Header from '@/components/common/MainContentHeader';
import LastUpdatedBadge from '@/components/common/LastUpdatedBadge';
import useI18n from '@/hooks/useI18n';
import { useForceRefresh } from '@/hooks/useForceRefresh';
import RefreshButton from '@/components/common/RefreshButton';
import { UserContext } from '@/context/UserContext';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Button } from '@/components/common/Button';
import CampaignDetailComponent from '@/components/common/campaign/CampaignDetailComponent';
import CampaignSubmissionsTableComponent from '@/components/common/campaign/CampaignSubmissionsTableComponent';
import AnalyticsComponent from '@/components/common/campaign/AnalyticsComponent';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import Loading from '@/components/common/Loading';
import { BrandMemberRole } from '@/types/enums';
import {
  useGetCampaignByIdQuery,
  useUpdateCampaignMutation,
} from '@/hooks/queries/useCampaignQueries';
import { useCampaignChartQuery } from '@/hooks/queries/useAnalyticsQueries';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/queries/queryKeys';
import type { DateFilteredData } from '@/hooks/useDateRangeQueryParams';

const Campaign = () => {
  const { t } = useI18n();
  const { id } = useParams();
  const userContext = useContext(UserContext);
  const queryClient = useQueryClient();
  const { refresh, isRefreshing, secondsRemaining } = useForceRefresh(
    // The campaign detail read caches its EMV, so it refreshes with the charts.
    () =>
      Promise.all([
        queryClient.refetchQueries({
          queryKey: ['analytics', 'campaign'],
          type: 'active',
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.campaigns.byId(id ?? ''),
          type: 'active',
        }),
      ]),
    `campaign-analytics:${id ?? ''}`,
  );
  const [isActionLoading, setIsActionLoading] = useState(false);
  // Shared CalendarFilter state — drives both the analytics charts and the
  // Campaign Submissions table (PROD-181). Mount default MUST match the
  // CalendarFilter `defaultFilter` prop in AnalyticsComponent so the initial
  // render's query key matches what CalendarFilter emits on mount.
  const [dateFilteredData, setDateFilteredData] = useState<DateFilteredData>({
    filter: 'all-time',
    startDate: null,
    endDate: null,
  });
  const handleDateFilterChange = useCallback(
    (startDate: Date | null, endDate: Date | null, filter: string) => {
      setDateFilteredData({ filter, startDate, endDate });
    },
    [],
  );
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;

  const { data: campaignResponse, isLoading: isCampaignLoading } =
    useGetCampaignByIdQuery(id || '', !!id);

  const campaign = campaignResponse?.campaign;
  const isLoading = isCampaignLoading || isActionLoading;

  // Same default filter AnalyticsComponent mounts with → React Query shares this cache entry,
  // no duplicate fetch. The badge only needs lastRefreshedAt which is filter-agnostic on the
  // backend (Redis-cached per campaign).
  const { data: campaignChartData } = useCampaignChartQuery(
    campaign?._id || '',
    'all-time',
    !!campaign?._id,
  );

  const updateMutation = useUpdateCampaignMutation();

  const isRegularBrandUser = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.User,
    [user?.brandMemberRole],
  );

  const pauseCampaign = async () => {
    if (!campaign?._id) return;

    setIsActionLoading(true);
    try {
      const updateData = { status: 'paused' as const };
      const res = await updateMutation.mutateAsync({
        id: campaign._id,
        updateData,
      });

      if (res) {
        toast.success(t('common.pauseSuccessMsg'));
        queryClient.invalidateQueries({
          queryKey: queryKeys.campaigns.byId(campaign._id),
        });
      }
    } catch (error) {
      toast.error(t('wallet.errors.spending'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const getCampaignStatusLabel = () => {
    if (!campaign?.status) return '';

    switch (campaign.status) {
      case 'live':
        return t('common.activeCampaign');
      case 'paused':
        return t('common.pausedCampaign');
      case 'completed':
        return t('common.completedCampaign');
      default:
        return t('common.activeCampaign');
    }
  };

  return (
    <div className=" h-[calc(100%-152px)]">
      <div className="p-[24px] lg:bg-[#F0F5FF] bg-white">
        <div className="flex items-start justify-between gap-4">
          <Header title="" subtitle={getCampaignStatusLabel()} />
          <div className="flex items-center gap-3">
            <LastUpdatedBadge
              lastRefreshedAt={campaignChartData?.lastRefreshedAt}
            />
            <RefreshButton
              onRefresh={refresh}
              isRefreshing={isRefreshing}
              secondsRemaining={secondsRemaining}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center p-[24px]">
        <div className="flex gap-3 cursor-pointer" onClick={handleBack}>
          <img
            src={'../assets/leftArrow.svg'}
            alt={String(t('campaign.navigation.backArrow'))}
          />
          <span className="text-[#113E53] text-sm">{t('common.back')}</span>
        </div>
        {campaign?.status === 'live' && !isRegularBrandUser && (
          <Button variant="outlined" onClick={pauseCampaign}>
            {t('common.pause')}
          </Button>
        )}
      </div>
      <div className="px-[24px] flex flex-col gap-4">
        {campaign && <CampaignDetailComponent campaign={campaign} />}
        <AnalyticsComponent
          campaign={campaign}
          dateFilteredData={dateFilteredData}
          onDateFilterChange={handleDateFilterChange}
        />
        <CampaignSubmissionsTableComponent
          campaign={campaign}
          dateFilteredData={dateFilteredData}
        />
      </div>
      {isLoading && <Loading fullScreen={true} />}
    </div>
  );
};

export default Campaign;
